/**
 * TemplateInsertMenu — sl-e0il.3.1
 *
 * Single shared Insert button used next to every CodeMirror / input
 * field in the config panels. Opens a popover with two tabs:
 *
 *   - Variables: upstream step output fields (from InsertableProvider)
 *   - Credentials: fields of the step's configured credential
 *
 * On click, the selected entry's literal text is inserted into the
 * associated editor via `editorRef.current.insertAtCursor(text)`. The
 * editor's `EditorAdapter` contract hides whether it is a CodeMirror
 * view, a plain `<input>`, or a future swap.
 *
 * DRY hooks:
 *   - Variable labels + insert text come from `formatStepFieldInsertText`
 *     which emits the unified `{{steps.<id>.<field>}}` template form
 *     (sl-fpi2.3). Every editor in the Workflow Builder — AI prompts,
 *     HTTP request, Code source, Service inputs — uses the same syntax;
 *     the Code runner pre-renders templates before sandbox execution.
 *   - Credential inserts run through an optional async gate
 *     (`onBeforeCredentialInsert`) so the AI-credential warning dialog
 *     (sl-e0il.3.2) can be wired in without this component knowing
 *     anything about AI or session storage.
 *
 * Zero-Fallbacks: if no editor ref is attached, the button does nothing
 * silently — there is no "fallback to append at end" path. The caller
 * is responsible for wiring the ref.
 */

import { Menu } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { PlusIcon } from '@blocksuite/icons/rc';
import type { RefObject } from 'react';
import { useMemo, useState } from 'react';

import { TypeChip } from '../../sidebar/components/type-chip';
import {
  formatCredentialInsertText,
  formatInputFieldInsertText,
  formatStepFieldInsertText,
} from './credential-syntax';
import type { EditorAdapter } from './editor-adapter';
import type {
  InsertableCredential,
  InsertableInputField,
  InsertableProvider,
  InsertableStepField,
} from './insertable';
import * as styles from './template-insert-menu.css';
import {
  filterCredentials,
  filterVariables,
  groupByStep,
  partition,
} from './template-insert-menu-helpers';

export interface TemplateInsertMenuProps {
  /** Ref to the editor adapter — `insertAtCursor` is called on click. */
  readonly editorRef: RefObject<EditorAdapter | null>;
  /**
   * Provides the list of insertables for the current step context.
   * The provider is constructed by `useAvailableInsertables` with the
   * current step id baked in, so the menu does not need (and must not
   * pass) a separate context id at call time.
   */
  readonly provider: InsertableProvider;
  /**
   * Optional async gate fired before a credential is inserted. If it
   * resolves to `false`, the insert is aborted. Used by the AI-node
   * call site to show the credential warning dialog.
   */
  readonly onBeforeCredentialInsert?: (
    credential: InsertableCredential
  ) => Promise<boolean>;
  readonly variablesEmptyLabel?: string;
  readonly disabled?: boolean;
}

type Tab = 'variables' | 'credentials';

export function TemplateInsertMenu({
  editorRef,
  provider,
  onBeforeCredentialInsert,
  variablesEmptyLabel,
  disabled,
}: TemplateInsertMenuProps) {
  const t = useI18n();
  const [tab, setTab] = useState<Tab>('variables');
  const [query, setQuery] = useState('');

  const insertables = useMemo(() => provider.getInsertables(), [provider]);

  const { variables, credentials } = useMemo(
    () => partition(insertables),
    [insertables]
  );

  const filteredVariables = useMemo(
    () => filterVariables(variables, query),
    [variables, query]
  );
  const filteredCredentials = useMemo(
    () => filterCredentials(credentials, query),
    [credentials, query]
  );

  const insertText = (text: string) => {
    editorRef.current?.insertAtCursor(text);
  };

  const handleVariableClick = (
    v: InsertableStepField | InsertableInputField
  ) => {
    insertText(
      v.kind === 'input-field'
        ? formatInputFieldInsertText(v.field.fieldId)
        : formatStepFieldInsertText(v.stepId, v.field.fieldId)
    );
  };

  const handleCredentialClick = async (c: InsertableCredential) => {
    if (onBeforeCredentialInsert) {
      const ok = await onBeforeCredentialInsert(c);
      if (!ok) return;
    }
    insertText(formatCredentialInsertText(c.credentialId, c.fieldName));
  };

  const content = (
    <div className={styles.menuContent}>
      <div className={styles.tabBar} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'variables'}
          className={styles.tabButton}
          onClick={() => setTab('variables')}
        >
          {t['com.streamlain.streamlain.workflow.insert-menu.variables-tab']()}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'credentials'}
          className={styles.tabButton}
          onClick={() => setTab('credentials')}
        >
          {t[
            'com.streamlain.streamlain.workflow.insert-menu.credentials-tab'
          ]()}
        </button>
      </div>
      <input
        type="text"
        className={styles.search}
        placeholder={t[
          'com.streamlain.streamlain.workflow.insert-menu.search'
        ]()}
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      {tab === 'variables' && (
        <VariableList
          items={filteredVariables}
          onPick={handleVariableClick}
          emptyLabel={
            variablesEmptyLabel ??
            t['com.streamlain.streamlain.workflow.insert-menu.no-variables']()
          }
        />
      )}
      {tab === 'credentials' && (
        <CredentialList
          items={filteredCredentials}
          onPick={c => {
            void handleCredentialClick(c);
          }}
          emptyLabel={t[
            'com.streamlain.streamlain.workflow.insert-menu.no-credentials'
          ]()}
        />
      )}
    </div>
  );

  return (
    <Menu items={content} contentOptions={{ side: 'bottom', align: 'start' }}>
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        aria-label={t[
          'com.streamlain.streamlain.workflow.insert-menu.trigger'
        ]()}
      >
        <PlusIcon />
        <span>
          {t['com.streamlain.streamlain.workflow.insert-menu.trigger']()}
        </span>
      </button>
    </Menu>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function VariableList({
  items,
  emptyLabel,
  onPick,
}: {
  items: readonly (InsertableStepField | InsertableInputField)[];
  emptyLabel: string;
  onPick: (v: InsertableStepField | InsertableInputField) => void;
}) {
  if (items.length === 0) {
    return <div className={styles.emptyState}>{emptyLabel}</div>;
  }
  const grouped = groupByStep(items);
  return (
    <div className={styles.list}>
      {grouped.map(group => (
        <div key={group.stepId}>
          <div className={styles.groupHeader}>{group.stepLabel}</div>
          {group.items.map(v => (
            <button
              type="button"
              key={`${v.kind}:${v.kind === 'step-field' ? v.stepId : 'input'}:${v.field.fieldId}`}
              className={styles.variableItem}
              onClick={() => onPick(v)}
              title={
                v.kind === 'input-field'
                  ? formatInputFieldInsertText(v.field.fieldId)
                  : formatStepFieldInsertText(v.stepId, v.field.fieldId)
              }
            >
              <span className={styles.itemField}>{v.field.name}</span>
              <TypeChip type={v.field.type} style={{ marginLeft: 'auto' }} />
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function CredentialList({
  items,
  emptyLabel,
  onPick,
}: {
  items: readonly InsertableCredential[];
  emptyLabel: string;
  onPick: (c: InsertableCredential) => void;
}) {
  if (items.length === 0) {
    return <div className={styles.emptyState}>{emptyLabel}</div>;
  }
  return (
    <div className={styles.list}>
      {items.map(c => (
        <button
          type="button"
          key={`${c.credentialId}:${c.fieldName}`}
          className={styles.item}
          onClick={() => onPick(c)}
          title={formatCredentialInsertText(c.credentialId, c.fieldName)}
        >
          <span className={styles.itemPrimary}>
            <span className={styles.itemField}>{c.credentialLabel}</span>
            <span className={styles.credentialFieldPill}>{c.fieldName}</span>
          </span>
          <span className={styles.itemMeta}>{c.credentialService}</span>
        </button>
      ))}
    </div>
  );
}
